import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Settings2,
  Sparkles,
  Zap,
} from 'lucide-react'
import { getTemplates } from '@/services/templates'
import {
  createGenerativeTriggers,
  getGenerativeTriggers,
} from '@/services/generative-triggers'
import { getCalendarIntegration } from '@/services/calendar-integration'
import { getChatInstance } from '@/services/chatIntegration'
import { getCatalogIntegration } from '@/services/catalog-integration'
import { getCRMIntegration } from '@/services/crmIntegration'
import { getMetaAdsIntegration } from '@/services/meta-ads-integration'
import { getFeegowIntegration } from '@/services/feegow-integration'
import { COLORS, INPUT_CLASS, INPUT_STYLE } from '../../shared'

const PAGE_SIZE = 8

const ACTION_LABELS = {
  WEBHOOK: 'Webhook',
  KNOWLEDGE: 'Conhecimento',
  CHAT_INTEGRATION: 'Chat',
  GOOGLE_CALENDAR: 'Google Agenda',
  CATALOG_INTEGRATION: 'Catálogo',
  NATIVE: 'Nativo',
  CRM: 'CRM',
  META_ADS: 'Meta Ads',
  FEEGOW: 'Feegow',
}

const REQUIREMENTS = {
  GOOGLE_CALENDAR: {
    integration: 'calendar',
    label: 'Google Agenda',
    check: (assistantID) => getCalendarIntegration({ assistantID }),
  },
  CHAT_INTEGRATION: {
    integration: 'chat',
    label: 'Chat de atendimento',
    check: (assistantID) => getChatInstance({ assistantID }),
  },
  CATALOG_INTEGRATION: {
    integration: 'catalog',
    label: 'Catálogo',
    check: (assistantID) => getCatalogIntegration(assistantID),
  },
  CRM: {
    integration: 'crm',
    label: 'CRM',
    check: (assistantID) => getCRMIntegration(assistantID),
  },
  META_ADS: {
    integration: 'meta-pixel',
    label: 'Meta Ads',
    check: (assistantID) => getMetaAdsIntegration({ assistantID }),
  },
  FEEGOW: {
    integration: 'feegow',
    label: 'Feegow',
    check: (assistantID) => getFeegowIntegration({ assistantID }),
  },
}

function hasItems(result) {
  const data = result?.data
  if (Array.isArray(data)) return data.length > 0
  return Boolean(data && (data.id || Object.keys(data).length))
}

function templateActionTypes(template) {
  return (template?.generativeActionTemplates || [])
    .map((action) => action?.type)
    .filter(Boolean)
}

export default function TemplateSetup({
  assistantID,
  installedTemplateIds,
  onProgress,
  onOpenIntegration,
  onTrack,
}) {
  const [templates, setTemplates] = useState([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [installingId, setInstallingId] = useState(null)
  const [installedIds, setInstalledIds] = useState(() => new Set(installedTemplateIds || []))
  const [requirements, setRequirements] = useState({})

  useEffect(() => {
    setInstalledIds((current) => new Set([...current, ...(installedTemplateIds || [])]))
  }, [installedTemplateIds])

  const loadRequirements = useCallback(async () => {
    if (!assistantID) return
    const checks = await Promise.all(
      Object.entries(REQUIREMENTS).map(async ([type, config]) => {
        try {
          const response = await config.check(assistantID)
          return [type, hasItems(response)]
        } catch {
          return [type, false]
        }
      })
    )
    setRequirements(Object.fromEntries(checks))
  }, [assistantID])

  const loadTemplates = useCallback(async () => {
    if (!assistantID) return
    setLoading(true)
    const [templatesResult, triggersResult] = await Promise.allSettled([
      getTemplates({ page, perPage: PAGE_SIZE, search, searchField: 'name' }),
      getGenerativeTriggers({ assistantId: assistantID, page: 1, perPage: 100 }),
    ])

    if (templatesResult.status === 'fulfilled') {
      setTemplates(templatesResult.value?.data || [])
      setTotal(Number(templatesResult.value?.total) || 0)
    } else {
      console.error(templatesResult.reason)
      toast.error('Não foi possível carregar os templates.')
    }

    if (triggersResult.status === 'fulfilled') {
      const ids = (triggersResult.value?.data || [])
        .map((trigger) => trigger?.generativeTriggerTemplateID)
        .filter(Boolean)
      const next = new Set([...(installedTemplateIds || []), ...ids])
      setInstalledIds(next)
      onProgress?.({ templateIds: Array.from(next) })
    }

    setLoading(false)
  }, [assistantID, installedTemplateIds, onProgress, page, search])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  useEffect(() => {
    loadRequirements()
  }, [loadRequirements])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const cards = useMemo(
    () => templates.map((template) => {
      const actions = templateActionTypes(template)
      const missing = actions
        .map((type) => ({ type, config: REQUIREMENTS[type] }))
        .filter(({ type, config }) => config && !requirements[type])
      return { template, actions, missing }
    }),
    [requirements, templates]
  )

  async function install(template) {
    setInstallingId(template.id)
    try {
      await createGenerativeTriggers(assistantID, {
        generativeTriggerTemplateID: template.id,
      })
      const next = new Set([...installedIds, template.id])
      setInstalledIds(next)
      onProgress?.({ templateIds: Array.from(next) })
      toast.success('Template instalado no agente.')
      onTrack?.('onboarding_template_installed')
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível instalar o template.')
    } finally {
      setInstallingId(null)
    }
  }

  function openRequirement(missing) {
    const first = missing[0]?.config
    if (!first) return
    onOpenIntegration?.(first.integration)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold" style={{ color: COLORS.text }}>
            Comece com uma automação pronta
          </p>
          <p className="mt-1 text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
            Templates usam as configurações do agente e podem ser ajustados depois.
          </p>
        </div>
        <label className="relative block w-full sm:w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Buscar template"
            className={`${INPUT_CLASS} h-10 pl-9 text-xs`}
            style={INPUT_STYLE}
          />
        </label>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[0, 1, 2, 3].map((item) => <div key={item} className="h-32 animate-pulse rounded-xl bg-white/[0.05]" />)}
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-xl border border-dashed px-4 py-8 text-center" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
          <Sparkles className="mx-auto h-5 w-5 text-white/35" />
          <p className="mt-2 text-sm font-semibold" style={{ color: COLORS.text }}>
            Nenhum template encontrado
          </p>
          <p className="mt-1 text-xs" style={{ color: COLORS.textMuted }}>
            Tente outra busca ou crie um gatilho depois na área do agente.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {cards.map(({ template, actions, missing }) => {
            const installed = installedIds.has(template.id)
            const blocked = missing.length > 0
            return (
              <article
                key={template.id}
                className="flex min-h-36 flex-col rounded-xl p-3"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex min-w-0 items-start gap-2">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(254,159,52,0.14)' }}>
                    <Zap className="h-3.5 w-3.5 text-[#FE9F34]" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold" style={{ color: COLORS.text }}>{template.name}</p>
                    <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
                      {template.description || 'Automação pronta para o seu agente.'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {actions.slice(0, 3).map((type) => (
                    <span key={type} className="rounded-md px-1.5 py-1 text-[9px] font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.57)' }}>
                      {ACTION_LABELS[type] || type}
                    </span>
                  ))}
                  {actions.length > 3 && <span className="px-1 py-1 text-[9px] text-white/40">+{actions.length - 3}</span>}
                </div>

                {blocked && (
                  <p className="mt-2 text-[10px] leading-relaxed" style={{ color: '#FFD18C' }}>
                    Requer: {missing.map(({ config }) => config.label).join(', ')}.
                  </p>
                )}

                <div className="mt-auto pt-3">
                  {installed ? (
                    <span className="flex h-8 items-center justify-center gap-1.5 rounded-lg text-[11px] font-semibold" style={{ background: 'rgba(52,254,173,0.1)', border: '1px solid rgba(52,254,173,0.24)', color: '#8FF2C7' }}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Instalado
                    </span>
                  ) : blocked ? (
                    <button
                      type="button"
                      onClick={() => openRequirement(missing)}
                      className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg text-[11px] font-semibold"
                      style={{ background: 'rgba(254,159,52,0.11)', border: '1px solid rgba(254,159,52,0.3)', color: '#FFD18C' }}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      Configurar pré-requisito
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={installingId === template.id}
                      onClick={() => install(template)}
                      className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ background: 'rgba(173,52,254,0.18)', border: '1px solid rgba(173,52,254,0.42)', color: '#E4C5FF' }}
                    >
                      {installingId === template.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                      Instalar
                    </button>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.1] text-white/60 disabled:opacity-35"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="text-[10px] text-white/45">{page} de {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.1] text-white/60 disabled:opacity-35"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
